import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressMembersMemberrolesMemberRoleDefinitionsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/members/v1/roles',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    'members.wixapps.net': [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/members/v1/roles',
        destPath: '/v1/roles',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_member-role-definition';

/**
 * Creates a new role definition.
 *
 * A role must be defined before it can be assigned to members using
 * [Assign Role](https://dev.wix.com/docs/rest/crm/members-contacts/members/role-v1/assign-role).
 * The `roleKey` must be unique across the site.
 */
export function createMemberRoleDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __createMemberRoleDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member_role_definition',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.memberroles.MemberRoleDefinitions.CreateMemberRoleDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersMemberrolesMemberRoleDefinitionsUrl({
        protoPath: '/v1/roles/definitions',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __createMemberRoleDefinition;
}

/**
 * Lists all role definitions for the site.
 *
 * Returns both system-defined roles and custom roles created via
 * [Create Member Role Definition](https://dev.wix.com/docs/rest/crm/members-contacts/members/member-role-definition-v1/create-member-role-definition).
 */
export function listMemberRoleDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __listMemberRoleDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member_role_definition',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.members.memberroles.MemberRoleDefinitions.ListMemberRoleDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersMemberrolesMemberRoleDefinitionsUrl({
        protoPath: '/v1/roles/definitions',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listMemberRoleDefinitions;
}

/**
 * Retrieves a role definition by its role key.
 *
 * To retrieve all role definitions for the site, use
 * [List Member Role Definitions](https://dev.wix.com/docs/rest/crm/members-contacts/members/member-role-definition-v1/list-member-role-definitions).
 */
export function getMemberRoleDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __getMemberRoleDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member_role_definition',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.members.memberroles.MemberRoleDefinitions.GetMemberRoleDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersMemberrolesMemberRoleDefinitionsUrl({
        protoPath: '/v1/roles/definitions/{roleKey}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getMemberRoleDefinition;
}

/**
 * Updates a role definition.
 *
 * Only the `blockable` field can be updated.
 * Pass the current `revision` value to prevent conflicting concurrent updates.
 * The `revision` is incremented on each successful update.
 * System roles cannot be updated.
 */
export function updateMemberRoleDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __updateMemberRoleDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member_role_definition',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.members.memberroles.MemberRoleDefinitions.UpdateMemberRoleDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersMemberrolesMemberRoleDefinitionsUrl({
        protoPath: '/v1/roles/definitions/{key}',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __updateMemberRoleDefinition;
}

/**
 * Deletes a role definition.
 *
 * Once deleted, the role can no longer be assigned to members.
 * Members who currently have the role retain it until it is explicitly unassigned.
 * System roles cannot be deleted.
 */
export function deleteMemberRoleDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteMemberRoleDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.member_role_definition',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.members.memberroles.MemberRoleDefinitions.DeleteMemberRoleDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersMemberrolesMemberRoleDefinitionsUrl({
        protoPath: '/v1/roles/definitions/{roleKey}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteMemberRoleDefinition;
}
